import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, Users, Shield, ArrowRight, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { US_STATES } from "@/data/pillars";
import { api } from "@/lib/api";

export const POOL_TIDS = {
    hubRoot: "labor-pool-hub",
    hubTotal: "labor-pool-hub-total",
    hubStateTile: "labor-pool-hub-state-tile",
    stateLanding: "labor-pool-state-landing",
    familyCta: "labor-pool-family-cta",
    caregiverCta: "labor-pool-caregiver-cta",
    agencyCta: "labor-pool-agency-cta",
};

const STATE_NAME = Object.fromEntries(US_STATES);

/**
 * /states — national hub. Shows every state we have supply in with counts.
 */
export function StatesHub() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/labor-pools").then((r) => setData(r.data)); }, []);

    const sorted = data ? Object.entries(data.states)
        .sort((a, b) => b[1].total - a[1].total) : [];

    return (
        <div data-testid={POOL_TIDS.hubRoot} className="min-h-screen bg-stone-50">
            <header className="border-b border-stone-200 bg-white">
                <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-emerald-700 flex items-center justify-center">
                            <HeartHandshake className="w-5 h-5 text-white" strokeWidth={1.75} />
                        </div>
                        <div className="leading-none">
                            <div className="font-outfit text-lg font-semibold tracking-tight text-stone-900">
                                Infini Health<span className="text-emerald-700">Care</span>
                            </div>
                            <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-stone-400 mt-0.5">
                                by CareCo 24
                            </div>
                        </div>
                    </Link>
                    <Link to="/auth?mode=register" className="text-sm text-emerald-700 font-outfit font-medium">
                        Get started →
                    </Link>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-12">
                <div className="text-center mb-10">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">
                        Labor pools · nationwide
                    </div>
                    <h1 className="font-outfit text-4xl sm:text-5xl font-semibold text-stone-900 tracking-tight">
                        Care that&rsquo;s <span className="text-emerald-700">already local</span> to you.
                    </h1>
                    {data && (
                        <p className="mt-4 text-lg text-stone-600 font-dmsans max-w-2xl mx-auto">
                            <span data-testid={POOL_TIDS.hubTotal} className="font-outfit font-semibold text-stone-900">
                                {data.totals.caregivers}
                            </span> vetted caregivers ready to work across{" "}
                            <span className="font-outfit font-semibold text-stone-900">
                                {data.totals.states} states
                            </span>
                            {" — pick yours."}
                        </p>
                    )}
                </div>

                {!data && <div className="text-center text-sm text-stone-500 font-dmsans">Loading state pools…</div>}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {sorted.map(([code, v]) => (
                        <Link
                            key={code}
                            to={`/state/${code}`}
                            data-testid={POOL_TIDS.hubStateTile}
                            data-state={code}
                            className="group rounded-2xl border border-stone-200 bg-white p-4 hover:border-emerald-400 hover:shadow-md transition-all"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-outfit text-xl font-semibold text-stone-900">{code}</span>
                                <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-emerald-700 group-hover:translate-x-0.5 transition-all" />
                            </div>
                            <div className="text-xs text-stone-500 font-dmsans truncate">{STATE_NAME[code] || code}</div>
                            <div className="mt-3 font-outfit text-2xl font-semibold text-emerald-700">
                                {v.total}
                            </div>
                            <div className="text-[10px] text-stone-400 uppercase tracking-wider font-bold">
                                caregivers
                                {v.verified > 0 && <span className="ml-1 text-emerald-600">· {v.verified} verified</span>}
                            </div>
                        </Link>
                    ))}
                </div>

                {sorted.length === 0 && data && (
                    <div className="text-center py-16 text-stone-500 font-dmsans">
                        Building our first pools now. Check back soon.
                    </div>
                )}
            </main>
        </div>
    );
}


/**
 * /state/:code — per-state landing / advertisement.
 */
export function StateLanding() {
    const { code } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const upper = (code || "").toUpperCase();

    useEffect(() => {
        api.get(`/labor-pools/${upper}`).then((r) => setData(r.data));
    }, [upper]);

    if (!data) return <div className="min-h-screen bg-stone-50 flex items-center justify-center text-sm text-stone-500 font-dmsans">Loading…</div>;

    const stateName = STATE_NAME[upper] || upper;
    const credOrder = ["RN", "LPN", "CNA", "HHA", "OTHER"];

    function seedSignupCtx(role, pillar) {
        try {
            localStorage.setItem("careco_signup_context", JSON.stringify({
                pillar, state: upper, service: "", role,
            }));
        } catch { /* noop */ }
        nav(`/auth?mode=register&role=${role}`);
    }

    return (
        <div data-testid={POOL_TIDS.stateLanding} data-state={upper} className="min-h-screen bg-stone-50">
            <header className="border-b border-stone-200 bg-white">
                <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-emerald-700 flex items-center justify-center">
                            <HeartHandshake className="w-5 h-5 text-white" strokeWidth={1.75} />
                        </div>
                        <div className="leading-none">
                            <div className="font-outfit text-lg font-semibold tracking-tight text-stone-900">
                                Infini Health<span className="text-emerald-700">Care</span>
                            </div>
                            <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-stone-400 mt-0.5">
                                by CareCo 24
                            </div>
                        </div>
                    </Link>
                    <Link to="/states" className="text-sm text-stone-700 font-outfit font-medium">← All states</Link>
                </div>
            </header>

            <section className="max-w-5xl mx-auto px-6 py-14 sm:py-20">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> {stateName} · Labor Pool
                </div>
                <h1 className="font-outfit text-4xl sm:text-6xl font-semibold text-stone-900 tracking-tight leading-tight">
                    <span className="text-emerald-700">{data.total}</span> vetted caregivers
                    <br />ready to work in {stateName}.
                </h1>
                {data.verified > 0 && (
                    <p className="mt-4 text-lg text-stone-600 font-dmsans">
                        <Shield className="w-4 h-4 inline text-emerald-700 mr-1 -mt-0.5" />
                        <span className="font-outfit font-semibold">{data.verified}</span> with background-check + CPR + insurance verified.
                    </p>
                )}

                {/* Credential mix strip */}
                {Object.keys(data.credential_mix).length > 0 && (
                    <div className="mt-8 flex flex-wrap gap-2">
                        {credOrder
                            .filter((c) => data.credential_mix[c])
                            .map((c) => (
                                <div key={c} className="rounded-full bg-white border border-stone-200 px-4 py-2">
                                    <span className="font-outfit font-semibold text-stone-900">{data.credential_mix[c]}</span>
                                    <span className="text-xs text-stone-500 font-dmsans ml-1">{c}</span>
                                </div>
                            ))}
                    </div>
                )}

                {/* 3-CTA grid */}
                <div className="mt-14 grid md:grid-cols-3 gap-4">
                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-2">Families</div>
                            <p className="text-sm text-stone-800 font-dmsans">{data.cta.families}</p>
                            <Button
                                data-testid={POOL_TIDS.familyCta}
                                onClick={() => seedSignupCtx("family", "home-health")}
                                className="mt-4 w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-10"
                            >
                                Book care in {upper}
                            </Button>
                        </CardContent>
                    </Card>
                    <Card className="rounded-2xl border-stone-200">
                        <CardContent className="p-6">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 mb-2">Facilities</div>
                            <p className="text-sm text-stone-800 font-dmsans">{data.cta.agencies}</p>
                            <Button
                                data-testid={POOL_TIDS.agencyCta}
                                onClick={() => seedSignupCtx("agency", "b2b-staffing")}
                                className="mt-4 w-full rounded-full bg-sky-800 hover:bg-sky-900 text-white h-10"
                            >
                                Fill shifts in {upper}
                            </Button>
                        </CardContent>
                    </Card>
                    <Card className="rounded-2xl border-stone-200 bg-orange-50/50">
                        <CardContent className="p-6">
                            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-700 mb-2">Caregivers</div>
                            <p className="text-sm text-stone-800 font-dmsans">{data.cta.caregivers}</p>
                            <Button
                                data-testid={POOL_TIDS.caregiverCta}
                                onClick={() => seedSignupCtx("caregiver", "b2b-staffing")}
                                className="mt-4 w-full rounded-full bg-orange-700 hover:bg-orange-800 text-white h-10"
                            >
                                <Users className="w-4 h-4 mr-1.5" /> Join {upper} pool
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-10 text-xs text-stone-500 font-dmsans text-center">
                    All caregivers are independently vetted. Home Health services are private pay only.
                </div>
            </section>
        </div>
    );
}
